// @ts-nocheck
import {memo, useCallback, useEffect, useId, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ChatCircle, CurrencyDollar, Heart} from "@phosphor-icons/react";
import useToggleGameFeedLike from "../../../../hooks/games/useToggleGameFeedLike.hook";
import {useAuthStore} from "../../../../store/useAuth.store";
import {useBalanceStore} from "../../../../store/useBalance.store";
import * as styles from "../_gameFeedCls";
import CommentsPanel from "./CommentsPanel";

const FeedCard = ({game, isActive, onRequestAuth}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const isAuthenticated = authStatus === "authenticated";
  const iframeId = useId();

  const [gameUrl, setGameUrl] = useState(null);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [localLiked, setLocalLiked] = useState(game.is_liked);
  const [localLikeCount, setLocalLikeCount] = useState(game.like_count);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gameInteractive, setGameInteractive] = useState(false);
  const iframeRef = useRef(null);
  const hasLaunched = useRef(false);
  const killTimerRef = useRef(null);
  const touchStartRef = useRef(null);

  const {toggleLike, isToggling} = useToggleGameFeedLike();
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);

  // Sync props to local state when data refreshes.
  useEffect(() => {
    setLocalLiked(game.is_liked);
    setLocalLikeCount(game.like_count);
  }, [game.is_liked, game.like_count]);

  // Hide bottom nav when in fullscreen; restore on exit or unmount.
  useEffect(() => {
    const bottomNav = document.querySelector(".bottomNavMenu");
    if (!bottomNav) return;
    bottomNav.style.visibility = isFullscreen ? "hidden" : "visible";
    return () => {
      bottomNav.style.visibility = "visible";
    };
  }, [isFullscreen]);

  const enterFullscreen = () => setIsFullscreen(true);
  const exitFullscreen = () => setIsFullscreen(false);

  // Auto-launch game when card becomes active and user is authenticated.
  useEffect(() => {
    if (isActive && isAuthenticated && !gameUrl && !isLaunching && !hasLaunched.current) {
      launchGame();
    }
  }, [isActive, isAuthenticated]);

  // Re-lock game interaction when card is no longer active.
  useEffect(() => {
    if (!isActive) setGameInteractive(false);
  }, [isActive]);

  // Kill inactive iframes after 3s to stop audio; restore src when active again.
  useEffect(() => {
    if (isActive) {
      // User scrolled back — cancel any pending kill and restore src.
      clearTimeout(killTimerRef.current);
      if (gameUrl && !iframeSrc) {
        setIframeSrc(gameUrl);
      }
    } else if (iframeSrc) {
      // Scrolled away — blank out iframe after 3 seconds.
      killTimerRef.current = setTimeout(() => {
        setIframeSrc(null);
        hasLaunched.current = false;
      }, 3000);
    }
    return () => clearTimeout(killTimerRef.current);
  }, [isActive, gameUrl, iframeSrc]);

  const launchGame = useCallback(async () => {
    if (!isAuthenticated) {
      onRequestAuth?.();
      return;
    }
    if (isLaunching || gameUrl) return;

    setIsLaunching(true);
    hasLaunched.current = true;
    try {
      const {ConfEnv} = await import("../../../../config/config");
      const useCurrencyStore = (await import("../../../../store/useCurrency.store")).default;
      const privateRequest = (await import("../../../../Infra/http/private.request")).default;

      const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
      const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";
      const isMobile = window.innerWidth <= 768;
      const url = `${ConfEnv.API_URL.GAMES_INIT}/${game.game_provider}/${game.game_id}?currency=${currencyParam}&is_mobile=${isMobile}`;
      const response = await privateRequest.get(url);
      const launchUrl = response?.data?.data?.gameUrl ?? null;
      setGameUrl(launchUrl);
      setIframeSrc(launchUrl);
    } catch {
      hasLaunched.current = false;
    } finally {
      setIsLaunching(false);
    }
  }, [isAuthenticated, game.game_provider, game.game_id, gameUrl, isLaunching]);

  const handleLike = () => {
    if (!isAuthenticated) {
      onRequestAuth?.();
      return;
    }
    // Optimistic update.
    setLocalLiked(prev => !prev);
    setLocalLikeCount(prev => (localLiked ? prev - 1 : prev + 1));
    toggleLike({gameId: game.game_id, gameProvider: game.game_provider});
  };

  // Touch overlay handlers: detect tap vs swipe.
  const handleOverlayTouchStart = e => {
    touchStartRef.current = {x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now()};
  };

  const handleOverlayTouchEnd = e => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    const dt = Date.now() - touchStartRef.current.time;
    touchStartRef.current = null;
    // Tap: small movement + short duration → unlock game for interaction.
    if (dx < 10 && dy < 10 && dt < 300) {
      setGameInteractive(true);
    }
  };

  const icon = game.custom_icon || game.icon;
  const providerInitial = game.game_provider?.charAt(0)?.toUpperCase() || "?";

  const feedTypeBadge = {
    hot: {emoji: "🔥", key: "gameFeedBadgeHot", className: styles.feedBadgeHotCls},
    new: {emoji: "✨", key: "gameFeedBadgeNew", className: styles.feedBadgeNewCls},
    big_winner: {emoji: "🏆", key: "gameFeedBadgeBigWinner", className: styles.feedBadgeBigWinnerCls},
  }[game.feed_type];

  return (
    <div className={styles.feedCardCls}>
      {/* Header — Provider info (like Instagram post header) */}
      <div className={styles.feedCardHeaderCls}>
        <div
          className={styles.providerAvatarCls}
          onClick={() => navigate(`/provider/${game.game_provider}`)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === "Enter" && navigate(`/provider/${game.game_provider}`)}
          style={{cursor: "pointer"}}
        >
          <div className={styles.providerAvatarInnerCls}>{providerInitial}</div>
        </div>
        <div className={styles.feedCardHeaderInfoCls}>
          <div
            className={styles.providerNameCls}
            onClick={() => navigate(`/provider/${game.game_provider}`)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === "Enter" && navigate(`/provider/${game.game_provider}`)}
            style={{cursor: "pointer"}}
          >
            {game.game_provider}
          </div>
          {game.game_name && <div className={styles.gameClassNameCls}>{game.game_name}</div>}
        </div>
        {feedTypeBadge && (
          <span className={`${styles.feedBadgeCls} ${feedTypeBadge.className}`}>
            <span>{feedTypeBadge.emoji}</span>
            <span>{t(feedTypeBadge.key)}</span>
          </span>
        )}
      </div>

      {/* Fullscreen overlay for mobile game rendering */}
      {isFullscreen && gameUrl && (
        <div className={styles.feedFullscreenOverlayCls}>
          <button
            type="button"
            className={styles.feedFullscreenCloseCls}
            onClick={exitFullscreen}
            aria-label={t("gameFeedCloseGame")}
          >
            ✕
          </button>
          <iframe
            id={`${iframeId}-fs`}
            src={gameUrl}
            className={styles.feedFullscreenIframeCls}
            title={game.game_name}
            allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
          />
        </div>
      )}

      {/* Game area — iframe persists once loaded to avoid re-init */}
      <div className={styles.feedGameAreaCls}>
        {/* Iframe: always rendered once gameUrl exists, visibility controlled by CSS */}
        {iframeSrc && (
          <iframe
            id={iframeId}
            ref={iframeRef}
            src={iframeSrc}
            className={`${styles.feedGameIframeCls} ${isActive && !isFullscreen ? (gameInteractive ? styles.feedGameIframeInteractiveCls : styles.feedGameIframeVisibleCls) : styles.feedGameIframeHiddenCls}`}
            title={game.game_name}
            allow="clipboard-read; clipboard-write; screen-wake-lock; accelerometer; gyroscope"
          />
        )}

        {/* Touch overlay — allows swiping to scroll; tap unlocks game interaction */}
        {iframeSrc && isActive && !isFullscreen && !gameInteractive && (
          <div
            className={styles.feedIframeTouchOverlayCls}
            onTouchStart={handleOverlayTouchStart}
            onTouchEnd={handleOverlayTouchEnd}
          />
        )}

        {/* Expand button floats over the iframe when active */}
        {iframeSrc && isActive && !isFullscreen && (
          <button
            type="button"
            className={styles.feedExpandButtonCls}
            onClick={enterFullscreen}
            aria-label={t("gameFeedExpandGame")}
          >
            <span className={styles.feedExpandIconCls}>⛶</span>
          </button>
        )}

        {/* Loading spinner */}
        {isLaunching && !gameUrl && (
          <div className={styles.feedLoadingGameCls}>
            <div className={styles.feedLoadingSpinnerCls} />
            <span className={styles.feedLoadingTextCls}>{t("gameFeedLoading")}</span>
          </div>
        )}

        {/* Preview thumbnail — only when no game has been loaded yet */}
        {!gameUrl && !isLaunching && (
          <div
            className={styles.feedGamePreviewCls}
            onClick={launchGame}
            role="button"
            tabIndex={0}
          >
            {icon ? (
              <img
                src={icon}
                alt={game.game_name}
                className={styles.feedGamePreviewImageCls}
              />
            ) : (
              <div
                className={styles.feedGamePreviewImageCls}
                style={{background: "#222", width: "60%", aspectRatio: "1", borderRadius: "var(--radius-xl)"}}
              />
            )}
            <div className={styles.playOverlayCls}>
              <div className={styles.playButtonCls}>
                <div className={styles.playIconCls} />
              </div>
            </div>
            <div className={styles.feedGameNameCls}>{game.game_name}</div>
          </div>
        )}
      </div>

      {/* Actions bar — like + comment */}
      <div className={styles.feedActionsBarCls}>
        <button
          type="button"
          className={styles.feedActionButtonCls}
          onClick={handleLike}
          disabled={isToggling}
          aria-label={localLiked ? t("gameFeedUnlike") : t("gameFeedLike")}
        >
          <Heart
            size={22}
            weight={localLiked ? "fill" : "regular"}
            color={localLiked ? "#ff4d6d" : "rgba(255,255,255,0.75)"}
          />
          {localLikeCount > 0 && <span className={styles.feedActionCountCls}>{localLikeCount}</span>}
        </button>

        <button
          type="button"
          className={styles.feedActionButtonCls}
          onClick={() => setShowComments(true)}
          aria-label={t("gameFeedComments")}
        >
          <ChatCircle
            size={22}
            weight="regular"
            color="rgba(255,255,255,0.75)"
          />
          {game.comment_count > 0 && <span className={styles.feedActionCountCls}>{game.comment_count}</span>}
        </button>

        {isAuthenticated && (
          <button
            type="button"
            className={`${styles.feedActionButtonCls} ${styles.feedDepositButtonCls}`}
            onClick={handleBankAccountModal}
            aria-label={t("gameFeedDeposit")}
          >
            <CurrencyDollar
              size={22}
              weight="fill"
              color="#fdee33"
            />
            <span className={styles.feedActionCountCls}>{t("gameFeedDeposit")}</span>
          </button>
        )}
      </div>

      {/* Comments panel (bottom sheet) */}
      {showComments && (
        <CommentsPanel
          provider={game.game_provider}
          gameId={game.game_id}
          onClose={() => setShowComments(false)}
        />
      )}
    </div>
  );
};

export default memo(FeedCard, (prev, next) => {
  return (
    prev.game.game_id === next.game.game_id &&
    prev.game.game_provider === next.game.game_provider &&
    prev.isActive === next.isActive
  );
});
